import { http, session, constants } from 'lib_ui-services';

const pending = new Set();
// Poll saved operation progress only during an explicit bulk action.
const PROGRESS_INTERVAL_MS = 5000;
const MAX_PROGRESS_CHECKS = { firmware: 720, reboot: 120, restartApp: 120, identify: 12 };
const TITLES = { firmware: 'Update firmware', reboot: 'Reboot readers', restartApp: 'Restart filter-reads', identify: 'Identify readers' };
export const _private = {
    http,
    onSessionChange: session.onSessionChange,
    wait: ms => new Promise(resolve => setTimeout(resolve, ms))
};

export default {
    verb: 'doingBulk',
    namespace: 'deviceManagement',
    relation: 'device',
    type: 'mark',
    description: 'Service selected readers and report each result independently',
    logic: serviceReaders
};

async function serviceReaders({ data, context, dispatch }) {
    const action = context.subtype?.replace(/^reader\./, '');
    const records = data.selection;
    if (!TITLES[action] || !records?.length) return;
    const group = context.session.groupNumber || 0;
    const scope = `${group}:${context.session.tenant?.['identity:tenant']?._id}`;
    if (pending.has(scope)) return;
    pending.add(scope);
    let active = true;
    let finishConfirmation;
    const cancel = () => {
        active = false;
        finishConfirmation?.(null);
    };
    const unsubscribe = _private.onSessionChange(cancel);
    const title = TITLES[action];
    const baseFor = record => `/g/${group}/api/deviceManagement/device/${record._id}`;
    const progress = (record, description, current = 0, total = 1) =>
        active &&
        dispatch(
            { mainTitle: `${title}: ${record.title}`, title: 'Progress', description, current, total },
            { verb: 'update', namespace: 'application', relation: 'progress' }
        );
    try {
        let choices;
        let availability;
        if (action === 'firmware') {
            availability = await Promise.allSettled(
                records.map(record => _private.http.get(`${baseFor(record)}/firmware`))
            );
            const available = availability.filter(item => item.status === 'fulfilled').map(item => item.value);
            if (!available.length) throw new Error('Unable to get firmware releases for the selected readers.');
            // Readers whose lookup failed are reported separately; the release
            // must support every reader whose available releases were returned.
            choices = available[0].releases.filter(release =>
                available.every(item => item.releases.some(r => r._id === release._id))
            );
            if (!choices.length)
                throw new Error('No firmware release supports every selected reader. Select readers of the same type.');
        }
        if (!active) return;
        const selected = await new Promise((resolve, reject) => {
            finishConfirmation = resolve;
            dispatch(
                {
                    title,
                    message:
                        action === 'identify'
                            ? `${records.length} readers selected. Flash their application LEDs for 30 seconds.`
                            : `${records.length} readers selected. Tag reading will be interrupted. ${action === 'firmware' ? 'Keep power and network connected until the update finishes.' : 'Each reader will be checked after it restarts.'}`,
                    ...(choices ? { choiceLabel: 'Firmware release', choices } : {}),
                    okButtonText: title,
                    cancelButtonText: 'Cancel',
                    okAction: value => resolve(choices ? value : true),
                    cancelAction: () => resolve(null)
                },
                { verb: 'confirm', namespace: 'application', relation: 'user' }
            ).catch(reject);
        });
        finishConfirmation = undefined;
        if (!selected || !active) return;
        // The service owns accepted operations. A failed reader must not prevent
        // other selected readers from starting or completing their actions.
        const results = await Promise.all(
            records.map(async (record, index) => {
                try {
                    if (availability?.[index].status === 'rejected') throw availability[index].reason;
                    await progress(record, 'Requesting…');
                    if (!active) return;
                    const base = baseFor(record);
                    const url = action === 'firmware' ? `${base}/firmware` : `${base}/diagnostics/actions`;
                    const operation = await _private.http.post(
                        url,
                        action === 'firmware'
                            ? { releaseId: selected._id, confirmed: true }
                            : { action, confirmed: true }
                    );
                    for (let attempt = 0; attempt < MAX_PROGRESS_CHECKS[action] && active; attempt++) {
                        const saved = await _private.http.get(action === 'firmware' ? `${url}/status` : url);
                        if (!active) return;
                        const current = saved.operation;
                        if (current?.operationId !== operation.operationId)
                            throw new Error('The action status has changed. Check this reader’s Health tab.');
                        await progress(
                            record,
                            current.status === 'active' ? 'In progress…' : current.status,
                            current.step || 0,
                            current.totalSteps || 1
                        );
                        if (current.status !== 'active') {
                            if (current.status === 'succeeded') {
                                await progress(record, 'Completed', 1, 1);
                                return `${record.title}: Completed`;
                            }
                            throw new Error(current.reason || 'Needs attention. Check this reader’s Health tab.');
                        }
                        await _private.wait(PROGRESS_INTERVAL_MS);
                    }
                    if (active) throw new Error('Still in progress. Check this reader’s Health tab.');
                } catch (error) {
                    await progress(record, error.message, 1, 1);
                    return `${record.title}: ${error.message}`;
                }
            })
        );
        await Promise.all(
            records.map(record =>
                dispatch(
                    { mainTitle: `${title}: ${record.title}` },
                    { verb: 'reset', namespace: 'application', relation: 'progress' }
                )
            )
        );
        if (active)
            await dispatch(
                {
                    title: `${title} results`,
                    message: results.filter(Boolean),
                    noCancelOption: true,
                    okButtonText: 'Close'
                },
                { verb: 'confirm', namespace: 'application', relation: 'user' }
            );
        return { ...data, results };
    } catch (error) {
        if (active)
            await dispatch(
                {
                    message: error.message || 'Unable to request reader actions.',
                    variant: 'error',
                    isError: true,
                    type: constants.notificationTypes.TOAST
                },
                { verb: 'pop', namespace: 'application', relation: 'notification' }
            );
        throw error;
    } finally {
        unsubscribe?.();
        pending.delete(scope);
        await Promise.all(
            records.map(record =>
                dispatch(
                    { mainTitle: `${title}: ${record.title}` },
                    { verb: 'reset', namespace: 'application', relation: 'progress' }
                )
            )
        );
    }
}
